import { useState } from 'react';
import type { ReactNode } from 'react';
import { CodeOutlined, DatabaseOutlined, DownloadOutlined, SearchOutlined } from '@ant-design/icons';

import { DataTable } from '../../../../components/shared';
import { Badge, Button, Dialog, Empty, Input, Skeleton } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import { luuTep } from '../../../../lib/luu-tep';
import { useToast } from '../../../../lib/toast';
import { useDongDuLieu, useXuatDuLieu } from '../../hooks/useLichSu';
import { chuTrongO, dongCsvDuLieu, laCotTrangThai, locDong, tenTepXuat } from '../../nghiep-vu/du-lieu';
import { taoCsv } from '../../nghiep-vu/csv';
import type { DongDuLieu, KhoaLuotDuLieu, LoaiDuLieu } from '../../types';
import { LoiRpa } from '../chung';

const CO_TRANG = ['20', '50', '100'];
const TRANG_MAC_DINH = 20;

function HopJson({ dong, onClose }: { dong: DongDuLieu; onClose: () => void }) {
  const { t, locale } = useT();
  return (
    <Dialog
      open
      width={640}
      onClose={onClose}
      title={t('rpaDuLieu.jsonTieuDe', { dong: soVN(dong.rowNo, locale) })}
      footer={
        <div className="flex justify-end">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.dong')}
          </Button>
        </div>
      }
    >
      <pre tabIndex={0} className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-all rounded-lg bg-subtle p-3 text-xs text-content-primary">
        {JSON.stringify(dong.data, null, 2)}
      </pre>
    </Dialog>
  );
}

/** Cột STT (số dòng gốc) + cột sinh theo thuộc tính JSON + nút xem JSON gốc. Cột ghép đánh dấu ★. */
function useCot(cot: readonly string[], xemJson: (d: DongDuLieu) => void, cotGhep: readonly string[] = []) {
  const { t, locale } = useT();
  return [
    {
      title: t('chung.stt'),
      key: '__stt',
      width: 64,
      fixed: 'left' as const,
      render: (_: unknown, d: DongDuLieu) => <span className="tabular-nums">{d.rowNo}</span>,
    },
    ...cot.map((c) => ({
      title: cotGhep.includes(c) ? (
        <span className="text-primary-600" title={t('rpaDuLieu.cotGhep')}>
          {c} ★
        </span>
      ) : (
        c
      ),
      key: c,
      render: (_: unknown, d: DongDuLieu): ReactNode => {
        const chu = chuTrongO(d.data[c], locale);
        if (laCotTrangThai(c) && chu) return <Badge variant="info">{chu}</Badge>;
        return (
          <span className="block max-w-xs truncate" title={chu}>
            {chu}
          </span>
        );
      },
    })),
    {
      title: <span className="sr-only">{t('rpaDuLieu.cotJson')}</span>,
      key: '__json',
      width: 64,
      fixed: 'right' as const,
      align: 'center' as const,
      render: (_: unknown, d: DongDuLieu) => (
        <Button
          variant="ghost"
          size="sm"
          icon={<CodeOutlined />}
          aria-label={t('rpaDuLieu.xemJson', { dong: soVN(d.rowNo, locale) })}
          title={t('rpaDuLieu.xemJson', { dong: soVN(d.rowNo, locale) })}
          onClick={() => xemJson(d)}
        />
      ),
    },
  ];
}

const luuJson = (ten: string, dong: readonly DongDuLieu[]) =>
  luuTep(new Blob([JSON.stringify(dong.map((d) => d.data), null, 2)], { type: 'application/json' }), ten);

const luuCsv = (ten: string, tieuDeStt: string, cot: readonly string[], dong: readonly DongDuLieu[]) =>
  luuTep(new Blob([taoCsv([tieuDeStt, ...cot], dongCsvDuLieu(cot, dong))], { type: 'text/csv;charset=utf-8' }), ten);

/** Bảng một loại dữ liệu (cào / xử lý) của một lượt: tìm và phân trang ở máy chủ, xuất JSON/CSV toàn bộ dòng khớp. */
export function BangDuLieu({ khoa, loai, luong, maLuot }: { khoa: KhoaLuotDuLieu; loai: LoaiDuLieu; luong: string; maLuot: string }) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const [tim, setTim] = useState('');
  const [trang, setTrang] = useState(1);
  const [coTrang, setCoTrang] = useState(TRANG_MAC_DINH);
  const [json, setJson] = useState<DongDuLieu | null>(null);
  const [dangXuat, setDangXuat] = useState<'csv' | 'json' | null>(null);
  const xuatHet = useXuatDuLieu();
  const q = useDongDuLieu(khoa, { kind: loai, search: tim, page: trang, pageSize: coTrang });
  const cot = useCot(q.data?.columns ?? [], setJson);
  const tieuDe = t(`rpaDuLieu.tab.${loai}`);

  const xuat = async (duoi: 'csv' | 'json') => {
    setDangXuat(duoi);
    try {
      const het = await xuatHet.mutateAsync({ ...khoa, kind: loai, search: tim });
      const ten = tenTepXuat(luong, maLuot, loai, duoi);
      if (duoi === 'json') luuJson(ten, het.data);
      else luuCsv(ten, t('chung.stt'), het.columns, het.data);
      toast.success(t('rpaDuLieu.daXuat', { so: soVN(het.total, locale) }));
    } catch (e) {
      toast.error(dichLoi(e));
    } finally {
      setDangXuat(null);
    }
  };

  const coDong = Boolean(q.data && q.data.total > 0);
  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          allowClear
          prefix={<SearchOutlined aria-hidden />}
          placeholder={t('rpaDuLieu.tim')}
          aria-label={t('rpaDuLieu.tim')}
          value={tim}
          onChange={(e) => {
            setTim(e.target.value);
            setTrang(1);
          }}
          className="w-full sm:w-64"
        />
        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            icon={<DownloadOutlined />}
            loading={dangXuat === 'json'}
            disabled={!coDong || dangXuat !== null}
            onClick={() => void xuat('json')}
          >
            {t('rpaDuLieu.xuatJson')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            icon={<DownloadOutlined />}
            loading={dangXuat === 'csv'}
            disabled={!coDong || dangXuat !== null}
            onClick={() => void xuat('csv')}
          >
            {t('rpaDuLieu.xuatCsv')}
          </Button>
        </div>
      </div>
      {q.isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : q.isError ? (
        <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />
      ) : !q.data || q.data.total === 0 ? (
        tim ? (
          <Empty icon={SearchOutlined} title={t('rpaDuLieu.rongLoc')} className="py-8" />
        ) : (
          <Empty icon={DatabaseOutlined} title={t(`rpaDuLieu.rong.${loai}`)} description={t('rpaDuLieu.rongMoTa')} className="py-8" />
        )
      ) : (
        <DataTable<DongDuLieu>
          caption={tieuDe}
          size="small"
          rowKey="rowNo"
          dataSource={q.data.data}
          loading={q.isFetching}
          pagination={{
            current: q.data.page,
            pageSize: q.data.pageSize,
            total: q.data.total,
            pageSizeOptions: CO_TRANG,
            showSizeChanger: true,
            showTotal: (tong, [tu, den]) =>
              t('rpaDuLieu.tongTrang', { tu: soVN(tu, locale), den: soVN(den, locale), tong: soVN(tong, locale) }),
            onChange: (moi, co) => {
              setTrang(co === coTrang ? moi : 1);
              setCoTrang(co);
            },
          }}
          columns={cot}
        />
      )}
      {json && <HopJson dong={json} onClose={() => setJson(null)} />}
    </div>
  );
}

/** Dòng thiếu / dư từ kết quả đối chiếu (đã có sẵn ở máy khách, tối đa 5.000 dòng): lọc, phân trang, xuất CSV. */
export function DongLech({
  dong,
  cot,
  tong,
  cotGhep,
  tenTep,
  rong,
  caption,
}: {
  dong: readonly DongDuLieu[];
  cot: readonly string[];
  tong: number;
  cotGhep: readonly string[];
  tenTep: string;
  rong: string;
  caption: string;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const [tim, setTim] = useState('');
  const [trang, setTrang] = useState(1);
  const [coTrang, setCoTrang] = useState(TRANG_MAC_DINH);
  const [json, setJson] = useState<DongDuLieu | null>(null);
  const cotBang = useCot(cot, setJson, cotGhep);
  const loc = locDong(dong, tim);

  if (!tong) return <Empty icon={DatabaseOutlined} title={rong} className="py-6" />;
  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          allowClear
          prefix={<SearchOutlined aria-hidden />}
          placeholder={t('rpaDuLieu.tim')}
          aria-label={t('rpaDuLieu.timTrong', { ten: caption })}
          value={tim}
          onChange={(e) => {
            setTim(e.target.value);
            setTrang(1);
          }}
          className="w-full sm:w-64"
        />
        {tong > dong.length && (
          <span className="text-xs text-warning-700">
            {t('rpaDuLieu.lechBiCat', { hien: soVN(dong.length, locale), tong: soVN(tong, locale) })}
          </span>
        )}
        <Button
          className="ml-auto"
          variant="outline"
          size="sm"
          icon={<DownloadOutlined />}
          disabled={loc.length === 0}
          onClick={() => {
            luuCsv(tenTep, t('chung.stt'), cot, loc);
            toast.success(t('rpaDuLieu.daXuat', { so: soVN(loc.length, locale) }));
          }}
        >
          {t('rpaDuLieu.xuatCsv')}
        </Button>
      </div>
      {loc.length === 0 ? (
        <Empty icon={SearchOutlined} title={t('rpaDuLieu.rongLoc')} className="py-6" />
      ) : (
        <DataTable<DongDuLieu>
          caption={caption}
          size="small"
          rowKey="rowNo"
          dataSource={[...loc]}
          pagination={{
            current: trang,
            pageSize: coTrang,
            pageSizeOptions: CO_TRANG,
            showSizeChanger: true,
            showTotal: (n, [tu, den]) =>
              t('rpaDuLieu.tongTrang', { tu: soVN(tu, locale), den: soVN(den, locale), tong: soVN(n, locale) }),
            onChange: (moi, co) => {
              setTrang(co === coTrang ? moi : 1);
              setCoTrang(co);
            },
          }}
          columns={cotBang}
        />
      )}
      {json && <HopJson dong={json} onClose={() => setJson(null)} />}
    </div>
  );
}
